import React, { useCallback, useEffect, useState } from "react";
import { ModeBadge } from "../../components/ModeBadge";
import { toast } from "sonner";
import { Fuel, RefreshCw, Wallet, Activity, ShieldAlert } from "lucide-react";
import axios, { API, fmt, fmtPct, pctColor, fmtTime, STRATEGY_LABELS } from "./commoditiesLib";
import { useAuth } from "../../auth/AuthContext";
import { EventCalendarCard } from "./EventCalendarCard";

const StatCard = ({ label, value, valueColor, sub }) => (
  <div className="border border-grid p-4">
    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data">{label}</div>
    <div className="font-heading text-2xl font-semibold mt-2" style={valueColor ? { color: valueColor } : undefined}>
      {value}
    </div>
    {sub && <div className="text-xs text-muted-2 mt-1 font-mono-data">{sub}</div>}
  </div>
);

const CommoditiesOverview = () => {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [kitePending, setKitePending] = useState([]);
  const [kiteBusyId, setKiteBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/commodities/overview`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Commodities overview");
    }
  }, []);

  // 2026-09-10 — Kite Connect confirm-gated CRUDEOIL orders (Phase 1, see
  // commodity_autopilot.py's KITE EXECUTION section). Polled separately
  // from the main overview since it's a distinct, admin-only concern —
  // most sessions will never see anything here (kill-switch defaults OFF).
  const loadKitePending = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/commodities/kite/pending`);
      setKitePending(data.pending || []);
    } catch (e) {
      // Silent — a non-admin viewer gets 403 on confirm/reject anyway, and
      // this list itself is read-only/non-actionable without admin rights.
    }
  }, []);

  useEffect(() => {
    load();
    loadKitePending();
    const t = setInterval(() => { load(); loadKitePending(); }, 30000);
    return () => clearInterval(t);
  }, [load, loadKitePending]);

  const confirmKiteOrder = async (id, action) => {
    const msg = action === "SELL"
      ? "Place a REAL Kite SELL order to EXIT this MCX position? This uses real money."
      : "Place a REAL Kite MCX order for this signal? This uses real money.";
    if (!window.confirm(msg)) return;
    setKiteBusyId(id);
    try {
      const { data } = await axios.post(`${API}/commodities/kite/confirm/${id}`);
      if (data.ok) {
        toast.success(`Kite ${action === "SELL" ? "exit" : "order"} placed — order_id ${data.order_id}`);
      } else {
        toast.error(data.message || data.error || "Kite order failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kite confirm failed");
    } finally {
      setKiteBusyId(null);
      // 2026-09-15 (chat: "need to implement 30 seconds refresh after order
      // placement") — both this page and the Live Orders page already poll
      // every 30s (see the useEffect above), but that meant a just-confirmed
      // order's own P&L/open-trades data on THIS page could sit stale for
      // up to 30s until the next tick. Refresh `load()` immediately too,
      // not just the pending-confirmations list, so the confirm itself
      // shows its effect right away instead of waiting on the interval.
      await Promise.all([load(), loadKitePending()]);
    }
  };

  const rejectKiteOrder = async (id) => {
    setKiteBusyId(id);
    try {
      await axios.post(`${API}/commodities/kite/reject/${id}`);
      toast.success("Dismissed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setKiteBusyId(null);
      await loadKitePending();
    }
  };

  const scanNow = async () => {
    setScanning(true);
    try {
      await axios.post(`${API}/commodities/scan`);
      toast.success("Commodities scan triggered");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setScanning(false);
    }
  };

  if (!data) return <div className="text-muted-2 font-mono-data text-xs">Loading…</div>;

  const pnl = data.pnl_overall || {};

  return (
    <section className="space-y-5" data-testid="commodities-overview-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            Commodities · MCX
          </div>
          <h2 className="font-heading text-2xl flex items-center gap-2">
            <Fuel size={20} /> Overview
          </h2>
        </div>
        {/* 2026-09-13 — chat: "Scan now option is only for Admin." Matches
            the require_admin gate added to POST /commodities/scan. */}
        {isAdmin && (
          <button
            onClick={scanNow}
            disabled={scanning}
            data-testid="commodities-scan-now"
            className="px-4 py-2 border text-[11px] uppercase tracking-[0.2em] font-mono-data flex items-center gap-2 disabled:opacity-50"
            style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
          >
            <RefreshCw size={12} className={scanning ? "animate-spin" : ""} /> Scan now
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Underlyings Watched" value={(data.underlyings || []).join(", ") || "—"} />
        <StatCard label="Open Positions" value={data.open_count ?? 0} />
        <StatCard
          label="Net P&L (closed, retention window)"
          value={fmt(pnl.net_pnl, 2)}
          valueColor={pctColor(pnl.net_pnl)}
          sub={`${pnl.trades ?? 0} trades · ${fmt(pnl.win_rate_pct, 0)}% win rate`}
        />
        <StatCard label="Scan Interval" value={`${data.scan_interval_sec ?? 60}s`} sub={`Retention: ${data.retention_days ?? "—"}d`} />
      </div>

      <EventCalendarCard isAdmin={isAdmin} />

      {kitePending.length > 0 && (
        <div className="border p-5 space-y-3" style={{ borderColor: "#FFB66E" }}>
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono-data flex items-center gap-2" style={{ color: "#FFB66E" }}>
            <ShieldAlert size={12} /> Kite Confirmation Needed — real order, real money
          </div>
          <div className="space-y-2">
            {kitePending.map((p) => {
              const isExit = p.action === "SELL";
              return (
                <div key={p.id} className="flex items-center justify-between gap-3 border-b border-grid/40 pb-2 text-xs font-mono-data">
                  <div>
                    <span
                      className="px-1.5 py-0.5 mr-1.5 text-[9px] uppercase tracking-wider border"
                      style={{ color: isExit ? "var(--bear)" : "var(--bull)", borderColor: isExit ? "var(--bear)" : "var(--bull)" }}
                    >
                      {isExit ? "Exit" : "Entry"}
                    </span>
                    <span className="font-semibold">{p.underlying}</span>{" "}
                    <span className="text-muted-2">{STRATEGY_LABELS[p.strategy] || p.strategy} · {p.direction} · {p.option_type} {fmt(p.strike, 0)} exp {p.expiry}</span>
                    <div className="text-muted-3 text-[10px] mt-0.5">
                      {isExit ? (
                        <>Exit signal: {p.close_reason} · SELL {p.kite_tradingsymbol} · qty {p.quantity} · expires {fmtTime(p.expires_at)}</>
                      ) : (
                        /* 2026-10-02 — live quote + how the real order is priced
                           (was the paper estimate with "estimate only"). */
                        <>
                          {p.market_premium != null ? <>Live quote ₹{fmt(p.market_premium)} · </> : null}
                          {(p.order_type || "").toUpperCase() === "LIMIT"
                            ? (p.market_premium != null && p.limit_offset
                                ? <>LIMIT ≈ ₹{fmt(p.market_premium - p.limit_offset, 0)} (quote − ₹{p.limit_offset} offset, re-priced at confirm)</>
                                : <>LIMIT at live quote (re-priced at confirm)</>)
                            : <>MARKET (offset not applied)</>}
                          {" "}· {p.quantity_lots} lot(s) · expires {fmtTime(p.expires_at)}
                        </>
                      )}
                    </div>
                    {/* 2026-09-22 (chat: "fix this with real P&L%") — the line
                        above is the SIMULATED Black-76 paper-trade close, which
                        can say "profit" while the REAL Kite position (bought at
                        a different real fill) is actually at a loss right now.
                        Shown only when the backend could resolve a real open
                        position for this exit (list_kite_pending's real_* fields
                        — absent, not zero, when it couldn't). */}
                    {isExit && p.real_pnl_pct != null && (
                      <div className="text-[10px] mt-0.5 font-semibold">
                        REAL: entry ₹{fmt(p.real_entry_price)} → now ₹{fmt(p.real_current_price)} · P&L{" "}
                        <span style={{ color: pctColor(p.real_pnl_pct) }}>{fmtPct(p.real_pnl_pct)}</span>
                        {p.real_pnl_rupees != null && (
                          <span style={{ color: pctColor(p.real_pnl_pct) }}> (₹{fmt(p.real_pnl_rupees)})</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => confirmKiteOrder(p.id, p.action)}
                      disabled={kiteBusyId === p.id}
                      className="px-3 py-1.5 border text-[10px] uppercase tracking-[0.2em] disabled:opacity-50"
                      style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => rejectKiteOrder(p.id)}
                      disabled={kiteBusyId === p.id}
                      className="px-3 py-1.5 border text-[10px] uppercase tracking-[0.2em] disabled:opacity-50"
                      style={{ color: "var(--bear)", borderColor: "var(--bear)" }}
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="border border-grid p-5 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data flex items-center gap-2">
          <Wallet size={12} /> Open Paper Positions
        </div>
        {(data.open_trades || []).length === 0 ? (
          <div className="text-xs text-muted-2 font-mono-data">No open positions.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-3 uppercase tracking-wider text-[10px] font-mono-data border-b border-grid">
                  <th className="px-3 py-2">Underlying</th>
                  <th className="px-3 py-2">Strategy</th>
                  <th className="px-3 py-2">Direction</th>
                  <th className="px-3 py-2">Leg</th>
                  <th className="px-3 py-2 text-right">Entry</th>
                  <th className="px-3 py-2 text-right">Current</th>
                  <th className="px-3 py-2 text-right">P&L%</th>
                  <th className="px-3 py-2">Entered</th>
                </tr>
              </thead>
              <tbody>
                {data.open_trades.map((t) => (
                  <tr key={t.id} className="border-b border-grid/40">
                    <td className="px-3 py-2 font-semibold">{t.underlying}<ModeBadge t={t} /></td>
                    <td className="px-3 py-2 text-muted-2">{STRATEGY_LABELS[t.signal_type] || t.signal_type}</td>
                    <td className="px-3 py-2" style={{ color: t.direction === "Bullish" ? "var(--bull)" : "var(--bear)" }}>
                      {t.direction}
                    </td>
                    <td className="px-3 py-2 text-muted-2">{t.option_type} {fmt(t.strike, 0)}</td>
                    <td className="px-3 py-2 text-right text-muted-2">
                      {fmt(t.entry_premium)}
                      {t.entry_pending ? (
                        <div className="text-[9px] font-mono-data" style={{ color: "var(--warn, #d9a441)" }}>LIMIT · not filled yet</div>
                      ) : t.live_avg_fill_price != null ? (
                        <div className="text-[9px] font-mono-data text-muted-3">Kite fill</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right text-muted-2">{fmt(t.current_premium)}</td>
                    <td className="px-3 py-2 text-right font-semibold" style={{ color: pctColor(t.pnl_pct) }}>
                      {fmtPct(t.pnl_pct)}
                    </td>
                    <td className="px-3 py-2 text-muted-3 font-mono-data text-[10px]">{fmtTime(t.entry_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="border border-grid p-5 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data flex items-center gap-2">
          <Activity size={12} /> Recent Scan Runs
        </div>
        {(data.recent_runs || []).length === 0 ? (
          <div className="text-xs text-muted-2 font-mono-data">No scan runs yet.</div>
        ) : (
          <div className="space-y-1.5">
            {data.recent_runs.map((r) => (
              <div key={r.id} className="border-b border-grid/40 pb-1.5">
                <div className="flex items-center justify-between text-xs font-mono-data">
                  <span className="text-muted-3">{fmtTime(r.ran_at_utc)}{r.manual ? " · manual" : ""}</span>
                  <span className="text-muted-2">
                    {(r.signals || []).length} signal(s) · {(r.entered || []).length} entered
                    {(r.near_misses || []).length > 0 && (
                      <span style={{ color: "#FFB66E" }}>
                        {" "}· {r.near_misses.length} filtered
                      </span>
                    )}
                    {(r.errors || []).length > 0 && (
                      <span style={{ color: "var(--bear)" }}> · {r.errors.length} error(s)</span>
                    )}
                  </span>
                </div>
                {/* 2026-08-26 — shown inline (not just on hover) per user
                    request: which filter rejected the pivot, and the exact
                    rvol vs floor, so it's visible at a glance instead of
                    requiring a mouseover to read. A pivot that confirmed
                    but got soft-rejected (RVOL filter, etc.) was previously
                    invisible entirely — this is why "TradingView showed a
                    signal, the site didn't" can now actually be diagnosed
                    from this panel. */}
                {(r.near_misses || []).length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {r.near_misses.map((m, i) => (
                      <div key={i} className="text-[10px] font-mono-data pl-2" style={{ color: "#FFB66E", borderLeft: "2px solid #FFB66E" }}>
                        {m.underlying} / {m.strategy} — {m.reason}
                        {m.rvol != null && m.min_rvol != null && (
                          <> (rvol {Number(m.rvol).toFixed(2)}x, needs ≥ {Number(m.min_rvol).toFixed(2)}x)</>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CommoditiesOverview;
